import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronLeft, Loader2, Save, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  createBlogPost,
  updateBlogPost,
  fetchAdminBlogPost,
} from "@/lib/api";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";

const CATEGORY_SUGGESTIONS = [
  "shadow-work",
  "journaling",
  "rituals",
  "practice",
  "essays",
  "product",
];

function slugify(text) {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s-]+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function AdminBlogEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    slug: "",
    meta_description: "",
    excerpt: "",
    body: "",
    category: "shadow-work",
    tags: "",
    author: "Umbra",
    hero_alt: "",
    published: true,
  });
  const [heroFile, setHeroFile] = useState(null);
  const [currentHeroUrl, setCurrentHeroUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [showPreview, setShowPreview] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const p = await fetchAdminBlogPost(id);
        setForm({
          title: p.title || "",
          slug: p.slug || "",
          meta_description: p.meta_description || "",
          excerpt: p.excerpt || "",
          body: p.body || "",
          category: p.category || "shadow-work",
          tags: (p.tags || []).join(", "),
          author: p.author || "Umbra",
          hero_alt: p.hero_alt || "",
          published: !!p.published,
        });
        setCurrentHeroUrl(p.hero_url || null);
        setSlugManuallyEdited(true);
      } catch {
        toast.error("Could not load post");
        navigate("/admin/blog");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const onTitleChange = (v) => {
    setForm((f) => ({
      ...f,
      title: v,
      slug: slugManuallyEdited ? f.slug : slugify(v),
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title required");
    if (!form.meta_description.trim())
      return toast.error("Meta description required (this shows in Google results)");
    if (form.meta_description.length > 300)
      return toast.error("Meta description must be ≤ 300 characters");
    if (!form.body.trim()) return toast.error("Body required");

    const fd = new FormData();
    fd.append("title", form.title);
    if (form.slug) fd.append("slug", form.slug);
    fd.append("meta_description", form.meta_description);
    fd.append("excerpt", form.excerpt);
    fd.append("body", form.body);
    fd.append("category", form.category);
    fd.append("tags", form.tags);
    fd.append("author", form.author);
    fd.append("hero_alt", form.hero_alt);
    fd.append("published", String(form.published));
    if (heroFile) fd.append("hero", heroFile);

    try {
      setBusy(true);
      if (isEdit) {
        await updateBlogPost(id, fd);
        toast.success("Post updated");
      } else {
        await createBlogPost(fd);
        toast.success("Post published");
      }
      navigate("/admin/blog");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen px-6 md:px-12 py-16">
        <p className="font-sans text-bone-500 text-sm">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link
          to="/admin/blog"
          className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-100"
        >
          <ChevronLeft className="h-3 w-3" /> Blog
        </Link>
        <div className="flex items-center gap-4">
          <AdminToggle />
          <UserBadge />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-12 pb-24">
        <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-2">
          {isEdit ? "Edit post" : "New post"}
        </p>
        <h1 className="font-serif text-4xl text-bone-100 mb-8">
          {isEdit ? "Edit blog entry" : "Compose a new entry"}
        </h1>

        <form onSubmit={onSubmit} className="space-y-5">
          <Field label="Title *">
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => onTitleChange(e.target.value)}
              data-testid="admin-blog-title"
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-serif text-2xl focus:outline-none"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Slug (URL)">
              <input
                type="text"
                value={form.slug}
                onChange={(e) => {
                  setSlugManuallyEdited(true);
                  setForm({ ...form, slug: slugify(e.target.value) });
                }}
                data-testid="admin-blog-slug"
                placeholder="auto from title"
                className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-mono text-sm focus:outline-none"
              />
              <p className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500 mt-1">
                /blog/{form.slug || "your-slug"}
              </p>
            </Field>

            <Field label="Category *">
              <input
                type="text"
                required
                list="blog-cat-suggestions"
                value={form.category}
                onChange={(e) =>
                  setForm({ ...form, category: e.target.value.toLowerCase() })
                }
                data-testid="admin-blog-category"
                className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
              />
              <datalist id="blog-cat-suggestions">
                {CATEGORY_SUGGESTIONS.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </Field>
          </div>

          <Field
            label={`Meta description * (${form.meta_description.length}/300)`}
            hint="Shows in Google search snippets. Aim for 140–160 chars, include primary keyword."
          >
            <textarea
              required
              value={form.meta_description}
              maxLength={300}
              onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
              rows={2}
              data-testid="admin-blog-meta"
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
            />
          </Field>

          <Field
            label={`Excerpt (${form.excerpt.length}/280)`}
            hint="One or two sentences shown on the blog index and RSS feed. Leave blank to auto-generate from the body."
          >
            <textarea
              value={form.excerpt}
              maxLength={280}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              rows={2}
              data-testid="admin-blog-excerpt"
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Tags (comma-separated)">
              <input
                type="text"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="shadow-work, jung, ritual"
                data-testid="admin-blog-tags"
                className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
              />
            </Field>
            <Field label="Author">
              <input
                type="text"
                value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
                data-testid="admin-blog-author"
                className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
              />
            </Field>
          </div>

          <Field label="Hero image" hint="16:9 or wider. JPG/PNG/WebP, ≤ 8 MB.">
            {currentHeroUrl && !heroFile && (
              <div className="mb-2">
                <img
                  src={currentHeroUrl}
                  alt={form.hero_alt || "Current hero"}
                  className="max-h-40 border border-obsidian-600"
                />
                <p className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500 mt-1">
                  Current — upload a new file to replace
                </p>
              </div>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setHeroFile(e.target.files[0] || null)}
              data-testid="admin-blog-hero"
              className="w-full font-sans text-sm text-bone-300 file:bg-obsidian-900 file:border file:border-obsidian-600 file:text-bone-100 file:px-3 file:py-2 file:mr-3 file:font-sans file:text-xs"
            />
          </Field>

          <Field
            label="Hero alt text"
            hint="Describe the image for screen readers and image search. Required if a hero image is present. e.g., 'a candle burning in a dim room, moth resting on the sill'."
          >
            <input
              type="text"
              value={form.hero_alt}
              onChange={(e) => setForm({ ...form, hero_alt: e.target.value })}
              maxLength={200}
              data-testid="admin-blog-hero-alt"
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
            />
          </Field>

          <Field
            label="Body (Markdown) *"
            hint="Supports GitHub-flavoured Markdown: headings (##), **bold**, *italic*, [links](url), lists, code, tables, images."
          >
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                data-testid="admin-blog-toggle-preview"
                className="inline-flex items-center gap-2 font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta"
              >
                <Eye className="h-3 w-3" />
                {showPreview ? "Hide preview" : "Show preview"}
              </button>
            </div>
            <div className={showPreview ? "grid grid-cols-1 md:grid-cols-2 gap-4" : ""}>
              <textarea
                required
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={22}
                data-testid="admin-blog-body"
                className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-mono text-sm focus:outline-none"
              />
              {showPreview && (
                <div
                  className="border border-obsidian-600 bg-obsidian-950/60 p-4 overflow-y-auto max-h-[500px] prose prose-invert prose-sm max-w-none font-serif"
                  data-testid="admin-blog-preview"
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {form.body || "*Preview appears here…*"}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </Field>

          <label className="flex items-center gap-2 font-sans text-xs text-bone-300">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              data-testid="admin-blog-published"
            />
            Published (visible on /blog and in sitemap)
          </label>

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-blog-submit"
              className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-5 px-8"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isEdit ? "Save changes" : "Publish post"}
            </Button>
            <Link
              to="/admin/blog"
              data-testid="admin-blog-cancel"
              className="font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-100 border-b border-transparent hover:border-bone-400 pb-0.5"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
        {label}
      </label>
      {children}
      {hint && (
        <p className="font-sans text-[0.65rem] text-bone-500 mt-1 italic">{hint}</p>
      )}
    </div>
  );
}
